import { createContext, ReactNode, useState } from 'react';
import useSearchQuery from '../hooks/useSearchQuery';

type ColumnWidth = number | { max?: number; min?: number; type?: 'percentage' | 'px' };

type CustomField = { element: JSX.Element; clickable_area: boolean };

type Field = string | number | boolean | JSX.Element | CustomField | null;

interface Row {
  id: string | number;
  selected?: boolean;
  selectable?: boolean;
  highlighted?: boolean;
  outdated?: boolean;
  fields?: Field[];
  widths?: ColumnWidth[];
  element?: React.ReactNode;
  has_error?: boolean;
  expendable?: boolean;
}

interface NestedTable {
  headers: Header[];
  rows: Row[];
}

type Header = string | JSX.Element;

// DynamicColumn is a string like "body.property-name" or "headers.property-name"
type DynamicColumn = string;

// Helper to parse a dynamic column string into its components (e.g. "body.sku" or "data.items:200")
interface ParsedDynamicColumn {
  column_string: string;
  root: string; // first path segment: "body", "query", "headers", "data", "metadata", etc.
  path: (string | number)[];
  display_name: string;
  width?: number;
}

const parseDynamicColumn = (column_string: string): ParsedDynamicColumn => {
  // Check if column_string contains a width specification (format: "field_path:width")
  let field_path = column_string;
  let width: number | undefined;

  const width_separator_index = column_string.lastIndexOf(':');
  if (width_separator_index !== -1) {
    const potential_width = column_string.substring(width_separator_index + 1);
    const parsed_width = parseInt(potential_width, 10);
    if (!isNaN(parsed_width) && parsed_width > 0) {
      width = parsed_width;
      field_path = column_string.substring(0, width_separator_index);
    }
  }

  const parts = field_path.split('.');
  const root = parts[0];
  const path: (string | number)[] = parts.slice(1);

  return {
    column_string,
    root,
    path,
    display_name: field_path,
    width,
  };
};

interface TableContextType {
  top_offset: number;
  headers: Header[];
  widths: ColumnWidth[];
  rows: (Row | NestedTable)[];
  dynamic_columns: DynamicColumn[];
  setTopOffset: (offset: number) => void;
  setHeaders: (headers: Header[]) => void;
  setWidths: (widths: ColumnWidth[]) => void;
  setRows: (rows: (Row | NestedTable)[]) => void;
  updateTableConfig: (config: Partial<TableConfig>) => void;
  addDynamicColumn: (column: DynamicColumn) => void;
  removeDynamicColumn: (column: DynamicColumn) => void;
  isDynamicColumnActive: (column: DynamicColumn) => boolean;
  reorderDynamicColumns: (active_index: number, over_index: number) => void;
  updateDynamicColumnWidth: (field_path: string, new_width: number) => void;
  has_scrolled: boolean;
}

interface TableConfig {
  top_offset: number;
  headers: Header[];
  widths: ColumnWidth[];
  rows: (Row | NestedTable)[];
  dynamic_columns?: DynamicColumn[];
}

const defaultContext: TableContextType = {
  top_offset: 0,
  headers: [],
  widths: [],
  rows: [],
  dynamic_columns: [],
  setTopOffset: () => undefined,
  setHeaders: () => undefined,
  setWidths: () => undefined,
  setRows: () => undefined,
  updateTableConfig: () => undefined,
  addDynamicColumn: () => undefined,
  removeDynamicColumn: () => undefined,
  isDynamicColumnActive: () => false,
  reorderDynamicColumns: () => undefined,
  updateDynamicColumnWidth: () => undefined,
  has_scrolled: false,
};

export const TableContext = createContext<TableContextType>(defaultContext);

interface TableProviderProps {
  children: ReactNode;
  initialConfig?: Partial<TableConfig>;
  has_scrolled: boolean;
}

export const TableProvider: React.FC<TableProviderProps> = ({
  children,
  initialConfig,
  has_scrolled,
}) => {
  const { query, updateSearchQuery } = useSearchQuery<{
    dynamic_columns?: DynamicColumn[] | null;
  }>();
  const [top_offset, setTopOffset] = useState<number>(initialConfig?.top_offset || 0);
  const [headers, setHeaders] = useState<Header[]>(initialConfig?.headers || []);
  const [widths, setWidths] = useState<ColumnWidth[]>(initialConfig?.widths || []);
  const [rows, setRows] = useState<(Row | NestedTable)[]>(initialConfig?.rows || []);

  // Derive dynamic_columns from URL query params - useSearchQuery handles serialization
  const dynamic_columns = (() => {
    // If dynamic_columns is in the query, use it
    // Note: null means "explicitly no columns" (overriding initialConfig)
    if ('dynamic_columns' in query) {
      if (query.dynamic_columns === null) {
        return [];
      }
      if (Array.isArray(query.dynamic_columns)) {
        return query.dynamic_columns as DynamicColumn[];
      }
      // Handle single string value (when there's only one column)
      if (typeof query.dynamic_columns === 'string') {
        return [query.dynamic_columns] as DynamicColumn[];
      }
    }
    return initialConfig?.dynamic_columns || [];
  })();

  const updateTableConfig = (config: Partial<TableConfig>) => {
    if (config.top_offset !== undefined) setTopOffset(config.top_offset);
    if (config.headers !== undefined) setHeaders(config.headers);
    if (config.widths !== undefined) setWidths(config.widths);
    if (config.rows !== undefined) setRows(config.rows);
    if (config.dynamic_columns !== undefined) {
      // Use null when removing all columns to explicitly override view defaults
      // Otherwise, use the provided array
      updateSearchQuery({
        dynamic_columns: config.dynamic_columns.length === 0 ? null : config.dynamic_columns,
      });
    }
  };

  const reorderDynamicColumns = (active_index: number, over_index: number) => {
    const new_columns = [...dynamic_columns];
    const [removed] = new_columns.splice(active_index, 1);
    new_columns.splice(over_index, 0, removed);
    updateSearchQuery({ dynamic_columns: new_columns });
  };

  const addDynamicColumn = (column: DynamicColumn) => {
    // Check if a column with the same field path already exists
    const target_field_path = column.split(':')[0];
    const already_exists = dynamic_columns.some((col) => {
      const col_field_path = col.split(':')[0];
      return col_field_path === target_field_path;
    });

    if (!already_exists) {
      const new_columns = [...dynamic_columns, column];
      updateSearchQuery({ dynamic_columns: new_columns });
    }
  };

  const removeDynamicColumn = (column: DynamicColumn) => {
    // Extract the field path without width for comparison
    const target_field_path = column.split(':')[0];
    const new_columns = dynamic_columns.filter((col) => {
      const col_field_path = col.split(':')[0];
      return col_field_path !== target_field_path;
    });
    // Use null when removing all columns to explicitly override view defaults
    // Otherwise, use the filtered array
    updateSearchQuery({ dynamic_columns: new_columns.length === 0 ? null : new_columns });
  };

  const updateDynamicColumnWidth = (field_path: string, new_width: number) => {
    const new_columns = dynamic_columns.map((col) => {
      const col_field_path = col.split(':')[0];
      if (col_field_path === field_path) {
        return `${field_path}:${Math.round(new_width)}`;
      }
      return col;
    });
    updateSearchQuery({ dynamic_columns: new_columns });
  };

  const isDynamicColumnActive = (column: DynamicColumn): boolean => {
    const target_field_path = column.split(':')[0];
    return dynamic_columns.some((col) => {
      const col_field_path = col.split(':')[0];
      return col_field_path === target_field_path;
    });
  };

  return (
    <TableContext.Provider
      value={{
        top_offset,
        headers,
        widths,
        rows,
        dynamic_columns,
        setTopOffset,
        setHeaders,
        setWidths,
        setRows,
        updateTableConfig,
        addDynamicColumn,
        removeDynamicColumn,
        isDynamicColumnActive,
        reorderDynamicColumns,
        updateDynamicColumnWidth,
        has_scrolled,
      }}>
      {children}
    </TableContext.Provider>
  );
};

export type {
  ColumnWidth,
  Field,
  Row,
  NestedTable,
  Header,
  TableConfig,
  DynamicColumn,
  ParsedDynamicColumn,
};

export { parseDynamicColumn };
